import * as React from "react"
import { GeolocationField as GeolocationFieldType } from "@/models/new/forms.types"
import { ResponseValue, GeolocationResponse } from "@/models/new/formResponse.types"
import { Label } from "@/components/shadcn/ui/label"
import { Button } from "@/components/shadcn/ui/button"
import { Loader2, LocateFixed, RefreshCw } from "lucide-react"

interface Props {
  field: GeolocationFieldType
  value?: ResponseValue
  onChange: (fieldId: string, value: ResponseValue) => void
  isCreating?: boolean
}

const GeolocationField = (props: Props) => {
  const { field, value } = props
  const [isLocating, setIsLocating] = React.useState(false)
  const [locationError, setLocationError] = React.useState<string>()

  const geolocation = value?.type === "geolocation" ? (value as GeolocationResponse).value : null
  const { latitude, longitude } = geolocation || { latitude: null, longitude: null }

  const hasValidCoordinates = typeof latitude === "number" && typeof longitude === "number"

  const googleMapsUrl = hasValidCoordinates
    ? `https://www.google.com/maps?q=${latitude},${longitude}`
    : ""

  const captureLocation = () => {
    if (!navigator.geolocation) {
      setLocationError(
        "Location is not supported by this browser. You can continue without adding it.",
      )
      return
    }

    setIsLocating(true)
    setLocationError(undefined)
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        props.onChange(field.id, {
          fieldName: field.name,
          type: "geolocation",
          value: { latitude: coords.latitude, longitude: coords.longitude },
        })
        setIsLocating(false)
      },
      (error) => {
        setLocationError(
          error.code === error.PERMISSION_DENIED
            ? "Location access is unavailable. You can continue without adding it."
            : "We couldn't determine your location. Try again or continue without adding it.",
        )
        setIsLocating(false)
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 },
    )
  }

  return (
    <React.Fragment>
      <div className="w-1/2 p-4">
        <div>
          <Label className="font-medium text-gray-700 mb-2">{field.label}</Label>
          {hasValidCoordinates ? (
            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <div className="flex items-center space-x-2 text-gray-600 mb-3">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="font-medium">Coordinates</span>
              </div>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,9rem),1fr))] gap-4 mb-4">
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-sm text-gray-500">Latitude</div>
                  <div className="font-mono text-gray-700">{latitude.toFixed(6)}°</div>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <div className="text-sm text-gray-500">Longitude</div>
                  <div className="font-mono text-gray-700">{longitude.toFixed(6)}°</div>
                </div>
              </div>
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                <span className="font-medium">View on Google Maps</span>
              </a>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 text-gray-500 flex items-center space-x-2">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>No location data available</span>
            </div>
          )}
          {props.isCreating ? (
            <div className="mt-3">
              <Button
                type="button"
                variant="outline"
                onClick={captureLocation}
                disabled={isLocating}
              >
                {isLocating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : hasValidCoordinates ? (
                  <RefreshCw className="h-4 w-4" />
                ) : (
                  <LocateFixed className="h-4 w-4" />
                )}
                {isLocating
                  ? "Finding location…"
                  : hasValidCoordinates
                    ? "Update location"
                    : "Use my location"}
              </Button>
              {!hasValidCoordinates ? (
                <p className="mt-2 text-sm text-gray-500">
                  Location is optional on the web. You can continue without adding it.
                </p>
              ) : null}
              {locationError ? (
                <p role="alert" className="mt-2 text-sm text-red-600">
                  {locationError}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </React.Fragment>
  )
}

export default GeolocationField
